import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReactCodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import DisplayBreadcrumbs from '../../components/displayBreadcrumbs/displayBreadcrumbs';
import {
  Script,
  EndpointType,
  EndpointTypeNames,
  EndpointSubtype,
  EndpointSubtypeNames,
  InputType,
  InputTypeNames,
  getDefaultScript,
  RunButtonState,
  FormState,
} from './types';
import { getAuthAutocomplete } from './helpers/codeEditorHelper';
import styles from './ScriptEditor.module.scss';
import {
  ScriptTestResponse,
  ScriptTestResultType,
} from '../../services/script/types';
import {
  getConsoleLogClassName,
  getRunButtonColor,
  scriptToForm,
} from './helpers/common';

const DEFAULT_FORM_STATE: FormState = {
  name: '',
  description: '',
  javascriptCode: '',
  sampleData: '{}',
  endpointTypeId: EndpointType.CRD,
  endpointSubtypeIds: [],
  inputTypeId: InputType.JSON,
};

const ScriptEditor: React.FC = () => {
  const { orgId: orgIdParam, id } = useParams<{
    orgId?: string;
    id?: string;
  }>();
  const history = useHistory();
  const isNew = !id || id === 'new';
  const isOrgContext = !!orgIdParam;

  const storeOrgId = useStoreState(
    (state: any) => state.organization.organizationId,
  );
  const orgId = orgIdParam ? decodeURIComponent(orgIdParam) : storeOrgId;
  const currentScript = useStoreState(
    (state: any) => state.script.currentScript,
  );
  const isRunning = useStoreState((state: any) => state.script.isRunning);
  const runResult: ScriptTestResponse | null = useStoreState(
    (state: any) => state.script.runResult,
  );

  const scriptAction = useStoreActions((actions: any) => actions.script);
  const commonAction = useStoreActions((actions: any) => actions.common);

  const [form, setForm] = useState<FormState>(DEFAULT_FORM_STATE);
  const {
    name,
    description,
    javascriptCode,
    sampleData,
    endpointTypeId,
    endpointSubtypeIds,
    inputTypeId,
  } = form;

  const setField = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const [runButtonState, setRunButtonState] = useState<RunButtonState>(
    RunButtonState.Run,
  );
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const savedScriptRef = useRef<Script | null>(null);

  useEffect(() => {
    if (isNew) {
      setForm({ ...DEFAULT_FORM_STATE, ...getDefaultScript() });
    } else if (savedScriptRef.current?.id === id) {
      scriptAction.setCurrentScript(savedScriptRef.current);
      savedScriptRef.current = null;
    } else {
      scriptAction.getScriptById({ orgId, id });
    }
    return () => {
      scriptAction.setCurrentScript(null);
      scriptAction.setRunResult(null);
    };
  }, [id, isNew, orgId, scriptAction]);

  useEffect(() => {
    if (currentScript && !isNew) {
      setForm(scriptToForm(currentScript));
    }
  }, [currentScript, isNew]);

  useEffect(() => {
    if (runResult?.consoleOutput?.length > 0) {
      setConsoleOpen(true);
    }
  }, [runResult]);

  const isDirty = useMemo(() => {
    if (isNew || !currentScript) return false;
    const saved = scriptToForm(currentScript);
    return (
      form.name !== saved.name ||
      form.description !== saved.description ||
      form.javascriptCode !== saved.javascriptCode ||
      form.endpointTypeId !== saved.endpointTypeId ||
      JSON.stringify(form.endpointSubtypeIds) !==
        JSON.stringify(saved.endpointSubtypeIds) ||
      form.inputTypeId !== saved.inputTypeId ||
      form.sampleData !== saved.sampleData
    );
  }, [isNew, currentScript, form]);

  const outputData = useMemo(() => {
    if (!runResult?.data) return '';
    try {
      return JSON.stringify(JSON.parse(runResult.data), null, 2);
    } catch {
      return runResult.data;
    }
  }, [runResult?.data]);

  const jsExtensions = useMemo(
    () => [javascript({ typescript: true }), getAuthAutocomplete()],
    [],
  );

  const jsonExtensions = useMemo(() => [json()], []);

  const backPath = useMemo(
    () =>
      isOrgContext
        ? `/organization/${encodeURIComponent(orgId)}?tab=2`
        : '/scripts',
    [isOrgContext, orgId],
  );

  const breadcrumbItems = useMemo(() => {
    if (isOrgContext) {
      const orgPath = `/organization/${encodeURIComponent(orgId)}?tab=2`;
      return [
        { title: 'Tenants', link: '/organization' },
        {
          title: 'Tenants Details',
          link: orgPath,
        },
        {
          title: 'Transformations',
          link: orgPath,
        },
        { title: isNew ? 'New' : name || 'Edit', link: '' },
      ];
    }
    return [
      { title: 'Scripts', link: '/scripts' },
      { title: isNew ? 'New Script' : name || 'Edit Script', link: '' },
    ];
  }, [isOrgContext, orgId, isNew, name]);

  const handleSave = useCallback(async () => {
    setSubmitAttempted(true);

    if (!form.name.trim()) {
      commonAction.setSubmitSnackbar({
        status: true,
        messages: 'Script name is required',
        error: true,
      });
      return;
    }

    if (form.endpointSubtypeIds.length === 0) {
      commonAction.setSubmitSnackbar({
        status: true,
        messages: 'At least one subtype must be selected',
        error: true,
      });
      return;
    }

    const payload: Omit<Script, 'lastModifiedDate'> & { id?: string } = {
      id: !isNew && currentScript?.id ? currentScript.id : undefined,
      ...form,
      name: form.name.trim(),
      description: form.description.trim(),
      javascriptCode: form.javascriptCode.trim(),
      sampleData: form.sampleData.trim(),
    };

    await scriptAction.saveScript({
      orgId,
      data: payload,
      commonAction,
      onSuccess: (saved: Script) => {
        if (isNew && saved?.id) {
          savedScriptRef.current = saved;
          const nextPath = isOrgContext
            ? `/organization/${encodeURIComponent(orgId)}/transformations/${
                saved.id
              }`
            : `/scripts/${saved.id}`;
          history.replace(nextPath);
        } else if (saved) {
          scriptAction.setCurrentScript(saved);
        }
      },
    });
  }, [
    form,
    isNew,
    currentScript?.id,
    scriptAction,
    orgId,
    commonAction,
    isOrgContext,
    history,
  ]);

  const handleTest = useCallback(async () => {
    if (isNew || !currentScript?.id) {
      commonAction.setSubmitSnackbar({
        status: true,
        messages: 'Please save the script before testing',
        error: true,
      });
      return;
    }
    setRunButtonState(RunButtonState.Running);
    try {
      const result: ScriptTestResponse | undefined =
        await scriptAction.testScript({
          orgId,
          id: currentScript.id,
          javascriptCode,
          data: sampleData,
          commonAction,
        });
      if (result?.code === ScriptTestResultType.FAILED && result.data) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages: result.data,
          error: true,
          timeout: 6000,
        });
      }
      setRunButtonState(
        result?.code === ScriptTestResultType.OK
          ? RunButtonState.Success
          : RunButtonState.Error,
      );
    } catch {
      setRunButtonState(RunButtonState.Error);
    } finally {
      setTimeout(() => setRunButtonState(RunButtonState.Run), 1000);
    }
  }, [
    orgId,
    javascriptCode,
    sampleData,
    scriptAction,
    commonAction,
    isNew,
    currentScript,
  ]);

  const getRunButtonIcon = () => {
    if (runButtonState === RunButtonState.Running) {
      return <StopIcon />;
    }
    return <PlayArrowIcon />;
  };

  return (
    <Grid container direction="row">
      <Grid item md={12} xs={12}>
        <DisplayBreadcrumbs items={breadcrumbItems} />
      </Grid>

      <Grid item md={12} style={{ padding: '10px 20px' }}>
        <Box className={styles.toolbar} mb="8px">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push(backPath)}
            size="small"
          >
            Back
          </Button>
          <TextField
            required
            label="Script name"
            value={name}
            onChange={(e) => setField('name', e.target.value)}
            size="small"
            variant="outlined"
            error={submitAttempted && !name.trim()}
            className={styles.nameInput}
          />
          <TextField
            value={description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder="Description"
            size="small"
            variant="outlined"
            className={styles.descriptionInput}
          />
          <FormControl
            size="small"
            variant="outlined"
            style={{ minWidth: 100 }}
          >
            <InputLabel>Type</InputLabel>
            <Select
              value={endpointTypeId}
              label="Type"
              onChange={(e) =>
                setField('endpointTypeId', e.target.value as EndpointType)
              }
            >
              {(
                Object.values(EndpointType).filter(
                  (v) => typeof v === 'number',
                ) as EndpointType[]
              ).map((v) => (
                <MenuItem key={v} value={v}>
                  {EndpointTypeNames[v]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            required
            size="small"
            variant="outlined"
            style={{ minWidth: 220 }}
            error={submitAttempted && endpointSubtypeIds.length === 0}
          >
            <InputLabel>Subtypes</InputLabel>
            <Select
              multiple
              value={endpointSubtypeIds}
              label="Subtypes"
              onChange={(e) =>
                setField(
                  'endpointSubtypeIds',
                  e.target.value as EndpointSubtype[],
                )
              }
              renderValue={(selected) =>
                (selected as EndpointSubtype[])
                  .map((v) => EndpointSubtypeNames[v])
                  .join(', ')
              }
            >
              {(
                Object.values(EndpointSubtype).filter(
                  (v) => typeof v === 'number',
                ) as EndpointSubtype[]
              ).map((v) => (
                <MenuItem key={v} value={v}>
                  {EndpointSubtypeNames[v]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" variant="outlined" style={{ minWidth: 90 }}>
            <InputLabel>Input</InputLabel>
            <Select
              value={inputTypeId}
              label="Input"
              onChange={(e) =>
                setField('inputTypeId', e.target.value as InputType)
              }
            >
              {(
                Object.values(InputType).filter(
                  (v) => typeof v === 'number',
                ) as InputType[]
              ).map((v) => (
                <MenuItem key={v} value={v}>
                  {InputTypeNames[v]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box
          flexDirection="row"
          justifyContent="flex-end"
          display="flex"
          gridColumnGap="4px"
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            size="small"
            style={
              isDirty
                ? { backgroundColor: '#f9a825', color: '#fff' }
                : undefined
            }
          >
            Save
          </Button>
          <Button
            variant="contained"
            color={getRunButtonColor(runButtonState)}
            startIcon={getRunButtonIcon()}
            onClick={handleTest}
            disabled={
              isNew ||
              !currentScript?.id ||
              isDirty ||
              (runButtonState === RunButtonState.Running && !isRunning)
            }
            size="small"
            className={styles.runButton}
          >
            Test
          </Button>
        </Box>
      </Grid>

      <Grid item md={12} style={{ padding: '0 20px' }}>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Box className={styles.panelHeader}>
              <Typography variant="subtitle2">Script (JavaScript)</Typography>
            </Box>
            <Box className={styles.editorPanel}>
              <ReactCodeMirror
                value={javascriptCode}
                height="100%"
                extensions={jsExtensions}
                onChange={(value: string) => setField('javascriptCode', value)}
              />
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box className={styles.panelHeader}>
              <Typography variant="subtitle2">Sample Data (JSON)</Typography>
            </Box>
            <Box className={styles.editorPanel}>
              <ReactCodeMirror
                value={sampleData}
                height="100%"
                extensions={jsonExtensions}
                onChange={(value: string) => setField('sampleData', value)}
              />
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box className={styles.panelHeader}>
              <Typography variant="subtitle2">Output</Typography>
            </Box>
            <Box className={styles.editorPanel}>
              <ReactCodeMirror
                value={outputData}
                height="100%"
                extensions={jsonExtensions}
                editable={false}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <Grid item md={12} style={{ padding: '8px 20px 20px 20px' }}>
        <Box
          className={styles.consoleHeader}
          onClick={() => setConsoleOpen(!consoleOpen)}
        >
          <Typography variant="subtitle2">Console </Typography>
          <IconButton size="small">
            {consoleOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={consoleOpen}>
          <Box className={styles.consoleBody}>
            {!runResult && (
              <Typography
                variant="body2"
                style={{ fontFamily: 'monospace', fontSize: 14 }}
              >
                Run a script to see console output...
              </Typography>
            )}
            {runResult?.consoleOutput?.map((line, idx) => (
              <div
                key={idx}
                className={`consoleText ${getConsoleLogClassName(line)}`}
              >
                <Typography style={{ fontFamily: 'monospace', fontSize: 14 }}>
                  {line}
                </Typography>
              </div>
            ))}
          </Box>
        </Collapse>
      </Grid>
    </Grid>
  );
};

export default ScriptEditor;
